import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import { cashAccountSchema, type CashAccountFormData } from '@/validators/cash'
import {
  useCashAccount,
  useCreateCashAccount,
  useUpdateCashAccount,
  useUpdateOpeningBalance,
  useDeactivateCashAccount,
  useActivateCashAccount,
} from '@/hooks/useCashAccounts'
import { FormSection } from '@/components/form/form-section'
import { MoneyInput } from '@/components/form/money-input'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { usePermissions } from '@/hooks/usePermissions'

function apiError(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  )
}

export function CashAccountFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const { has } = usePermissions()
  const isAdmin = has('cash.manage_opening_balance')

  const { data: account, isLoading: loadingAccount } = useCashAccount(id ?? '')
  const createMutation = useCreateCashAccount()
  const updateMutation = useUpdateCashAccount(id ?? '')
  const openingBalanceMutation = useUpdateOpeningBalance(id ?? '')
  const deactivateMutation = useDeactivateCashAccount()
  const activateMutation = useActivateCashAccount()

  const [statusDialog, setStatusDialog] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CashAccountFormData>({
    resolver: zodResolver(cashAccountSchema),
    defaultValues: {
      name: '',
      type: 'CASH',
      openingBalance: '' as unknown as number,
      isDefault: false,
    },
  })

  useEffect(() => {
    if (account) {
      reset({
        name: account.name,
        type: account.type,
        openingBalance: Number.parseFloat(account.openingBalance) as unknown as number,
        isDefault: account.isDefault,
      })
    }
  }, [account, reset])

  const balanceLocked = isEdit && !isAdmin
  const defaultLocked = isEdit && !!account?.isDefault

  async function onSubmit(data: CashAccountFormData) {
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(data)
        if (isAdmin && account && data.openingBalance !== Number.parseFloat(account.openingBalance)) {
          await openingBalanceMutation.mutateAsync(data.openingBalance)
        }
        toast.success('Conta atualizada!')
      } else {
        await createMutation.mutateAsync(data)
        toast.success('Conta cadastrada!')
      }
      navigate('/cash/accounts')
    } catch (err) {
      toast.error(apiError(err, 'Erro ao salvar conta'))
    }
  }

  async function handleToggleStatus() {
    if (!account) return
    try {
      if (account.active) {
        await deactivateMutation.mutateAsync(account.id)
        toast.success('Conta desativada')
      } else {
        await activateMutation.mutateAsync(account.id)
        toast.success('Conta reativada')
      }
      setStatusDialog(false)
      navigate('/cash/accounts')
    } catch (err) {
      toast.error(apiError(err, 'Erro ao alterar status'))
    }
  }

  if (isEdit && loadingAccount) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  const mutation = isEdit ? updateMutation : createMutation

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate('/cash/accounts')}>
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? 'Editar conta' : 'Nova conta'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? account?.name : 'Cadastre uma conta de caixa ou banco'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <FormSection title="Dados da conta">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="name">Nome *</Label>
              <Input id="name" aria-invalid={!!errors.name} {...register('name')} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="type">Tipo *</Label>
              <select
                id="type"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                {...register('type')}
              >
                <option value="CASH">Dinheiro (caixa físico)</option>
                <option value="BANK">Conta bancária</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="openingBalance">Saldo inicial *</Label>
            <MoneyInput
              id="openingBalance"
              error={!!errors.openingBalance}
              disabled={balanceLocked}
              {...register('openingBalance')}
            />
            {errors.openingBalance && (
              <p className="text-xs text-destructive">{errors.openingBalance.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {isEdit
                ? balanceLocked
                  ? 'Apenas um administrador pode alterar o saldo inicial.'
                  : 'O saldo inicial é o valor real da conta hoje.'
                : 'Informe o valor que a conta tem hoje (ex.: dinheiro em caixa).'}
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              disabled={defaultLocked}
              {...register('isDefault')}
            />
            <span>Conta padrão (usada nos lançamentos automáticos)</span>
          </label>
          {defaultLocked && (
            <p className="text-xs text-muted-foreground">
              Esta já é a conta padrão. Defina outra conta como padrão para alterar.
            </p>
          )}
        </FormSection>

        <div className="flex items-center justify-between gap-2">
          <div>
            {isEdit && account && (
              <Button
                type="button"
                variant={account.active ? 'destructive' : 'outline'}
                onClick={() => setStatusDialog(true)}
              >
                {account.active ? 'Desativar conta' : 'Reativar conta'}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/cash/accounts')}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <LoadingButton type="submit" loading={mutation.isPending} loadingText="Salvando…">
              {isEdit ? 'Salvar alterações' : 'Cadastrar conta'}
            </LoadingButton>
          </div>
        </div>
      </form>

      <ConfirmDialog
        open={statusDialog}
        title={account?.active ? 'Desativar conta' : 'Reativar conta'}
        description={
          account?.active
            ? 'A conta deixará de aparecer nas seleções de lançamentos. O histórico é preservado.'
            : 'A conta voltará a ficar disponível para novos lançamentos.'
        }
        confirmLabel={account?.active ? 'Desativar' : 'Reativar'}
        destructive={account?.active}
        loading={deactivateMutation.isPending || activateMutation.isPending}
        onConfirm={handleToggleStatus}
        onCancel={() => setStatusDialog(false)}
      />
    </div>
  )
}
